import { Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { GetEventOperationsByPlaceIdResponseData } from "#resources/api/enterprise-generated";
import { sortBy } from "#resources/helpers/sort";

import Icon from "#components/icon";
import { Button } from "#components/primitive/button";
import { formatDate } from "#resources/helpers/date-format";
import { useFinancialFileState } from "../../financial-file-context";
import AutoDownloadPDF from "../auto-pdf-download/auto-pdf-download";
import s from "./financial-file-table.scss";

const PER_PAGE = 10;

export function FinancialFileTable() {
	const { t } = useTranslation("place", { keyPrefix: "financialFile" });
	const {
		financialFileItems,
		loading,
		pagination,
		onChangePagination,
		requestClosingOfEventOperationData,
		setCurrentEventOperationId,
		clearCurrentEventOperationId,
	} = useFinancialFileState();

	const columns: ColumnProps<GetEventOperationsByPlaceIdResponseData>[] = [
		{
			title: t("table.columns.name").toUpperCase(),
			key: "name",
			sorter: (a, b) => sortBy(a, b, "name"),
			render: (_t, r) => r.name,
		},
		{
			title: t("table.columns.contractId").toUpperCase(),
			key: "contractNumber",
			sorter: (a, b) => sortBy(a, b, "contractNumber"),
			render: (_t, r) => r.contractNumber,
		},
		{
			title: t("table.columns.period").toUpperCase(),
			key: "beginAt",
			sorter: (a, b) => sortBy(a, b, "beginAt"),
			render: (_t, r) =>
				r.beginAt && r.endAt ? `${formatDate(r.beginAt)} - ${formatDate(r.endAt)}` : "",
		},
		{
			title: "",
			key: "actions",
			render: (_t, r) => (
				<Button
					variant="ghost"
					leadingIcon={<Icon icon="download" />}
					onClick={() => setCurrentEventOperationId(r.id)}
				>
					{t("downloadButton")}
				</Button>
			),
			align: "center",
			width: "5%",
		},
	];

	return (
		<div className={s.tableContainer}>
			<Table<GetEventOperationsByPlaceIdResponseData>
				sortDirections={["ascend", "descend"]}
				columns={columns}
				dataSource={financialFileItems}
				loading={loading}
				className={s.table}
				pagination={{
					className: s.pagination,
					current: pagination?.currentPage ?? 1,
					pageSize: pagination?.perPage ?? PER_PAGE,
					onChange: page =>
						onChangePagination({
							currentPage: page - 1,
							perPage: pagination?.perPage ?? PER_PAGE,
						}),
					total: pagination?.total ?? 0,
					showSizeChanger: false,
					showTotal: (total, range) => {
						if (range[0] === range[1]) {
							return `${range[0]} de ${total} itens`;
						}
						return `${range[0]}-${range[1]} de ${total} itens`;
					},
				}}
			/>
			<AutoDownloadPDF
				data={requestClosingOfEventOperationData}
				onDownloadComplete={clearCurrentEventOperationId}
			/>
		</div>
	);
}
